/**
 * API Client - Centralized HTTP client with error handling, timeout, and cancellation support
 *
 * For static export, this client calls the backend API directly.
 * Configure the backend URL via NEXT_PUBLIC_API_URL environment variable.
 */
import { traceparentFor } from '@/lib/telemetry';
import { API_CONFIG } from '@/config/api';
import { RATE_LIMIT_MAX_RETRIES, RATE_LIMIT_STATUS, resolveRetryDelayMs } from './rateLimit';
import { handleSessionExpired, refreshToken } from './tokenRefreshService';

export interface ApiError {
  message: string;
  status: number;
  code?: string;
  details?: Record<string, unknown>;
}

export class ApiClientError extends Error {
  status: number;
  code?: string;
  details?: Record<string, unknown>;

  constructor(error: ApiError) {
    super(error.message);
    this.name = 'ApiClientError';
    this.status = error.status;
    this.code = error.code;
    this.details = error.details;
  }

  get isNetworkError() {
    return this.status === 0;
  }

  get isServerError() {
    return this.status >= 500;
  }

  get isClientError() {
    return this.status >= 400 && this.status < 500;
  }

  get isNotFound() {
    return this.status === 404;
  }

  get isUnauthorized() {
    return this.status === 401;
  }

  get isForbidden() {
    return this.status === 403;
  }
}

/**
 * Sleep for `ms` milliseconds, but reject with an AbortError if the supplied
 * signal aborts first. The outer request() catch turns AbortError into the
 * existing TIMEOUT/NETWORK_ERROR path so callers see the same surface.
 */
const sleepWithSignal = (ms: number, signal: AbortSignal): Promise<void> =>
  new Promise<void>((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException('Aborted', 'AbortError'));
      return;
    }
    const onAbort = (): void => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    signal.addEventListener('abort', onAbort, { once: true });
  });

export type QueryParams = Record<string, string | number | boolean | undefined>;

export interface RequestConfig extends Omit<RequestInit, 'body'> {
  timeout?: number;
  params?: QueryParams;
  body?: unknown;
  // Skip the global 401 → token-refresh → session-expired-overlay pipeline
  // for this single request. Use for auth-probe endpoints like /me where a
  // 401 is the answer to the question "am I authorised?" — not a stale-token
  // signal that should drive a session-expired UX.
  skipAuthRetry?: boolean;
  // When set, the client sends `X-Operation-Id: <value>` so backend services
  // can join logs/traces on a single logical user action and emits
  // `api.request` / `api.response` / `api.error` / `api.retry` console events
  // for client-side correlation.
  operationId?: string;
}

interface ApiClientConfig {
  baseUrl: string;
  defaultTimeout?: number;
  defaultHeaders?: Record<string, string>;
}

export class ApiClient {
  private baseUrl: string;
  private defaultTimeout: number;
  private defaultHeaders: Record<string, string>;

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl;
    this.defaultTimeout = config.defaultTimeout ?? 30000;
    this.defaultHeaders = {
      'Content-Type': 'application/json',
      ...config.defaultHeaders,
    };
  }

  private buildUrl(
    endpoint: string,
    params?: Record<string, string | number | boolean | undefined>
  ): string {
    // Add /api/v1 prefix if not already present
    const apiVersion = '/api/v1';
    const fullPath = endpoint.startsWith(apiVersion) ? endpoint : `${apiVersion}${endpoint}`;

    // If no baseUrl, use relative URLs (same-origin requests)
    if (!this.baseUrl) {
      if (!params || Object.keys(params).length === 0) {
        return fullPath;
      }
      // Add query params to relative URL
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          searchParams.append(key, String(value));
        }
      });
      return `${fullPath}?${searchParams.toString()}`;
    }

    const url = new URL(fullPath, this.baseUrl);

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined) {
          url.searchParams.append(key, String(value));
        }
      });
    }

    return url.toString();
  }

  private async handleResponse<T>(
    response: Response,
    retryFn?: () => Promise<Response>,
    isRetry = false,
    skipAuthRetry = false
  ): Promise<T> {
    if (!skipAuthRetry && response.status === 401 && !isRetry && retryFn) {
      // Attempt to refresh the token before giving up
      const result = await refreshToken();
      if (result.success) {
        // Token refreshed — replay the original request
        const retryResponse = await retryFn();
        return this.handleResponse<T>(retryResponse, undefined, true, skipAuthRetry);
      }
      // Refresh failed — show session expired overlay
      handleSessionExpired(result.logoutUrl);
    } else if (!skipAuthRetry && response.status === 401) {
      // Already retried or no retry function — session is expired
      handleSessionExpired();
    }

    if (!response.ok) {
      let errorData: Record<string, unknown> = {};

      try {
        const contentType = response.headers.get('content-type');
        if (contentType?.includes('application/json')) {
          errorData = await response.json();
        } else {
          const textError = await response.text();
          errorData = { message: textError };
        }
      } catch {
        errorData = { message: response.statusText || 'Request failed' };
      }

      // Extract details without duplicating message, error, and code fields
      const { error, message, code, details, ...otherFields } = errorData;

      // Combine details with other fields if present
      const errorDetails = {
        ...(details && typeof details === 'object' ? (details as Record<string, unknown>) : {}),
        ...(Object.keys(otherFields).length > 0 ? otherFields : {}),
      };

      throw new ApiClientError({
        // Backends in this repo use both shapes: some send only `error` (a
        // human string), some send `error` (a code like "unauthorized") plus a
        // descriptive `message`. Prefer the human-readable `message` when
        // present so toasts don't surface terse codes.
        message: (message || error || 'Request failed') as string,
        status: response.status,
        code: code as string | undefined,
        details: Object.keys(errorDetails).length > 0 ? errorDetails : undefined,
      });
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      // Try to parse as JSON, but fall back to text if it fails
      // This handles cases where the server incorrectly sets content-type
      const text = await response.text();
      try {
        return JSON.parse(text);
      } catch {
        // If JSON parsing fails, return the raw text
        // This can happen with diagnostics endpoints that return text with wrong content-type
        return text as unknown as T;
      }
    }

    return response.text() as unknown as T;
  }

  async request<T>(endpoint: string, config: RequestConfig = {}): Promise<T> {
    const {
      timeout = this.defaultTimeout,
      params,
      body,
      headers,
      signal,
      skipAuthRetry = false,
      operationId,
      ...init
    } = config;

    const method = (init.method as string | undefined) ?? 'GET';
    const requestStart = Date.now();

    // Computed once per request, not per retry attempt: every attempt of one
    // user action belongs to the same trace, exactly as it shares one
    // operationId. Null when operationId is absent or is not a UUID.
    const traceparent = operationId ? traceparentFor(operationId) : null;

    // No-op unless `operationId` is set on this request's config — emits
    // `api.request` / `api.response` / `api.error` / `api.retry` console
    // events keyed by that id so a single user action can be traced through
    // DevTools alongside the X-Operation-Id header sent to the backend.
    const traceLog = (
      kind: 'api.request' | 'api.response' | 'api.error' | 'api.retry',
      extra: Record<string, unknown>
    ): void => {
      if (!operationId) return;
      const payload = {
        operationId,
        endpoint,
        method,
        ...extra,
        timestamp: new Date().toISOString(),
      };
      if (kind === 'api.error') {
        console.warn(kind, payload);
      } else {
        console.info(kind, payload);
      }
    };

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);

    // If caller provided a signal, abort our controller when it fires
    if (signal) {
      if (signal.aborted) {
        controller.abort();
      } else {
        signal.addEventListener('abort', () => controller.abort(), { once: true });
      }
    }

    try {
      const url = this.buildUrl(endpoint, params);
      const fetchOptions: RequestInit = {
        ...init,
        credentials: 'include',
        headers: {
          ...this.defaultHeaders,
          ...headers,
          ...(operationId ? { 'X-Operation-Id': operationId } : {}),
          // W3C traceparent whose trace id is the operation id. Envoy fronts
          // the API with tracing on and only generates a trace id when the
          // request has none — so without this it invents one, and compute-api's
          // span lands in Envoy's trace rather than this operation's.
          //
          // traceparentFor returns null when the operation id is not a UUID
          // (newOperationId has a non-UUID fallback for browsers without Web
          // Crypto), in which case we send nothing and Envoy generates as before.
          ...(traceparent ? { traceparent } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
        signal: controller.signal,
      };

      traceLog('api.request', {});

      // Build a retry function that replays the same request
      const retryFn = async (): Promise<Response> => {
        return fetch(url, fetchOptions);
      };

      // Outer 429 retry loop. Honour Retry-After when supplied, otherwise
      // fall back to capped exponential backoff. handleResponse handles 401
      // and (when retries are exhausted) extracts the backend's error/message
      // fields from the 429 body so the user sees the backend's own message.
      let response = await fetch(url, fetchOptions);
      for (let attempt = 0; attempt < RATE_LIMIT_MAX_RETRIES; attempt++) {
        if (response.status !== RATE_LIMIT_STATUS) break;
        const delay = resolveRetryDelayMs(response.headers.get('Retry-After'), attempt);
        traceLog('api.retry', { attempt: attempt + 1, delayMs: delay, reason: 'rate_limit' });
        await sleepWithSignal(delay, controller.signal);
        response = await fetch(url, fetchOptions);
      }

      try {
        const result = await this.handleResponse<T>(response, retryFn, false, skipAuthRetry);
        traceLog('api.response', {
          status: response.status,
          latencyMs: Date.now() - requestStart,
        });
        return result;
      } catch (innerError) {
        const status = innerError instanceof ApiClientError ? innerError.status : 0;
        const code = innerError instanceof ApiClientError ? innerError.code : undefined;
        const message = innerError instanceof Error ? innerError.message : String(innerError);
        traceLog('api.error', { status, code, message, latencyMs: Date.now() - requestStart });
        throw innerError;
      }
    } catch (error) {
      if (error instanceof ApiClientError) {
        throw error;
      }

      if (error instanceof Error) {
        if (error.name === 'AbortError') {
          traceLog('api.error', {
            status: 0,
            code: 'TIMEOUT',
            message: 'Request timeout',
            latencyMs: Date.now() - requestStart,
          });
          throw new ApiClientError({
            message: 'Request timeout',
            status: 0,
            code: 'TIMEOUT',
          });
        }

        traceLog('api.error', {
          status: 0,
          code: 'NETWORK_ERROR',
          message: error.message || 'Network error',
          latencyMs: Date.now() - requestStart,
        });
        throw new ApiClientError({
          message: error.message || 'Network error',
          status: 0,
          code: 'NETWORK_ERROR',
        });
      }

      traceLog('api.error', {
        status: 0,
        code: 'UNKNOWN',
        message: 'Unknown error',
        latencyMs: Date.now() - requestStart,
      });
      throw new ApiClientError({
        message: 'Unknown error',
        status: 0,
        code: 'UNKNOWN',
      });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async get<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'POST', body });
  }

  async put<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PUT', body });
  }

  async patch<T>(endpoint: string, body?: unknown, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'PATCH', body });
  }

  async delete<T>(endpoint: string, config?: RequestConfig): Promise<T> {
    return this.request<T>(endpoint, { ...config, method: 'DELETE' });
  }
}

// Create and export singleton instance
// For static export, always use API_CONFIG.baseUrl (backend server)
export const apiClient = new ApiClient({
  baseUrl: API_CONFIG.baseUrl,
  defaultTimeout: API_CONFIG.timeout,
});

export default apiClient;
